import { Button, Checkbox, Input, Select } from '@calizahq/react-components-v2'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'

import { TimeInputControlled } from '@/components'
import { ScheduledCommunicationTypeMutations } from '@/graphql/mutations/scheduledCommunicationType.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import {
  NewScheduledCommunicationTypeDetails,
  ScheduledCommunicationTypeBaselineMetricTypes,
  ScheduledCommunicationTypeChannels,
  ScheduledCommunicationTypeScheduleUnits,
} from '@/types/ScheduledCommunicationType'
import { formatDate } from '@/util'

import { formSchema, FormValuesType, initialValues } from './CreateScheduledCommunicationTypeForm.util'
import { CreateScheduledCommunicationTypeConfirmation } from './CreateScheduledCommunicationTypeFormConfirmation'

type Props = {
  refetchOnSuccess?: () => void
  toggle: () => void
}

export const CreateScheduledCommunicationTypeForm = ({ toggle, refetchOnSuccess }: Props) => {
  const [error, setError] = useState<string>()
  const [scheduledCommunicationType, setScheduledCommunicationType] = useState<NewScheduledCommunicationTypeDetails>()

  const { isTablet } = useMobile()

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(
    ScheduledCommunicationTypeMutations.createScheduledCommunicationType(),
    {
      onSuccess: (res: { createScheduledCommunicationType: NewScheduledCommunicationTypeDetails }) => {
        setScheduledCommunicationType(res.createScheduledCommunicationType)
        refetchOnSuccess && refetchOnSuccess()
      },
      onError: (res: { response: { errors: { message: string }[] } }) => {
        const { response } = res
        if (response.errors && response.errors.length) {
          setError(response.errors[0].message)
        }
      },
    },
  )

  const scheduledCommunicationTypeChannelOptions = Object.values(ScheduledCommunicationTypeChannels).map(
    (scheduledCommunicationTypeChannel) => {
      return { label: scheduledCommunicationTypeChannel, value: scheduledCommunicationTypeChannel }
    },
  )

  const scheduledCommunicationTypeScheduleUnitOptions = Object.values(ScheduledCommunicationTypeScheduleUnits).map(
    (scheduledCommunicationTypeScheduleUnit) => {
      return { label: scheduledCommunicationTypeScheduleUnit, value: scheduledCommunicationTypeScheduleUnit }
    },
  )

  const scheduledCommunicationTypeBaselineMetricOptions = Object.values(
    ScheduledCommunicationTypeBaselineMetricTypes,
  ).map((scheduledCommunicationTypeBaselineMetric) => {
    return { label: scheduledCommunicationTypeBaselineMetric, value: scheduledCommunicationTypeBaselineMetric }
  })

  const handleSubmit = (values: FormValuesType) => {
    const { timeToSend, ...restOfValues } = values
    const formattedTimeToSend = timeToSend ? formatDate(timeToSend, 'kk:mm:ss') : null
    mutate({
      scheduledCommunicationType: {
        timeToSend: formattedTimeToSend,
        ...restOfValues,
      },
    })
  }

  return (
    <>
      {scheduledCommunicationType ? (
        <CreateScheduledCommunicationTypeConfirmation
          scheduledCommunicationType={scheduledCommunicationType}
          toggle={toggle}
        />
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...initialValues,
          }}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validationSchema={formSchema}
        >
          {() => {
            return (
              <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-160">
                <p className="leading-nonemt-4 mb-4 font-bold text-header-md">New Scheduled Communication Type</p>

                <Checkbox className="mb-4 w-full" id="active" label="Active?" name="active" />
                <div className="flex w-full flex-col grid-cols-2 md:gap-4">
                  <Select
                    className="mb-4 w-full"
                    id="scheduledCommunicationChannel"
                    label="Channel"
                    name="scheduledCommunicationChannel"
                    options={scheduledCommunicationTypeChannelOptions}
                  ></Select>
                </div>
                <Input
                  className="mb-4 w-full"
                  id="leadTime"
                  label="Lead Time (in days)"
                  min={0}
                  name="leadTime"
                  type="number"
                />
                <div className="flex w-full mb-4 grid-cols-3 flex-row md:gap-4">
                  <Input
                    className="w-1/4"
                    id="numberOfScheduleUnits"
                    label="Number of units"
                    name="numberOfScheduleUnits"
                    type="number"
                  />
                  <Select
                    className="mb-4 w-1/4"
                    id="scheduleUnits"
                    label="Schedule units"
                    name="scheduleUnits"
                    options={scheduledCommunicationTypeScheduleUnitOptions}
                  ></Select>
                  <Select
                    className="mb-4 w-1/2"
                    id="baselineMetric"
                    label="Baseline metric"
                    name="baselineMetric"
                    options={scheduledCommunicationTypeBaselineMetricOptions}
                  ></Select>
                </div>
                <TimeInputControlled
                  className="mb-4 w-full"
                  endHour={18}
                  label="Time to Send (local to the Reservation)"
                  name="timeToSend"
                  startHour={8}
                  timeFormat="HH:mm:ss"
                  timeIntervals={30}
                />
                <Checkbox
                  className="mb-4 w-full"
                  id="defaultToApproved"
                  label="Default to approved?"
                  name="defaultToApproved"
                />

                {error && <p className=" mb-4 text-p-sm text-text-semantic-error">{error}</p>}
                <div className="flex flex-col-reverse justify-between md:flex-row">
                  <Button
                    className="w-full bg-action-secondary md:flex md:w-auto"
                    onClick={toggle}
                    size={isTablet ? 'medium' : 'small'}
                    variant="secondary"
                  >
                    Close
                  </Button>
                  <Button
                    className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                    disabled={isMutationLoading}
                    isLoading={isMutationLoading}
                    size={isTablet ? 'medium' : 'small'}
                    type="submit"
                  >
                    Create
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </>
  )
}
